import type { ConditionalOperator, ConditionScope } from "@repo/shared";
import {
  FOLLOWUP_MODE_SOURCE,
  GOAL_HAS_RISK_SOURCE,
  GOAL_RISK_SCORE_CAPPED_SOURCE,
  GOAL_RISK_SCORE_SOURCE,
  GOAL_TYPE_SOURCE,
  getLocalizedValue,
  isConditionAllowedInScope,
  isConditionSourceCustomFieldType,
  isDanglingProductAssessmentSource,
  isDanglingQuizSource,
  isOptionFieldType,
  isProductAssessmentSource,
  isQuizResultSource,
  isScoreConditionSource,
  isValueRequiredOperator,
  menuConditionOperator,
  operatorForNewSource,
  PORTFOLIO_SCHEDULE_SOURCE,
  PORTFOLIO_TYPE_SOURCE,
  pickT,
  QUIZ_RESULT_SOURCE_PREFIX,
  resolveConditionSourceScope,
  scalarConditionOperators,
} from "@repo/shared";
import type { CustomFieldOption, FlowField } from "@repo/shared/types";
import { useEffect } from "react";
import {
  assessmentSourceValueOptions,
  buildAssessmentPickerEntries,
  buildQuizScorePickerEntries,
  quizResultValueOptions,
  scoreSourcePassScore,
} from "@/components/conditional-display/assessment-picker-entries";
import { conditionRuleProblemMessage } from "@/components/conditional-display/condition-rule-problem-message";
import { GoalTypeConditionPicker } from "@/components/conditional-display/goal-type-condition-picker";
import { ScalarOperatorItems } from "@/components/conditional-display/scalar-operator-items";
import { ScoreConditionValueInput } from "@/components/conditional-display/score-condition-value-input";
import { useCountryOptions } from "@/components/country-select";
import { DatePicker } from "@/components/ui/date-picker";
import { DateTimePicker } from "@/components/ui/date-time-picker";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SearchableSelect, type SearchableSelectOption } from "@/components/ui/searchable-select";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { useFlowConfiguredAssessmentTypes } from "@/lib/hooks/use-configured-assessment-types";
import { useGoalTypes } from "@/lib/hooks/use-portfolio-collections";
import { useFlowQuizzes } from "@/lib/hooks/use-quizzes";
import { useUserLanguage } from "@/lib/i18n/context";
import type { PageType } from "@/lib/types/flow";
import { T } from "./conditional-visibility-fields.i18n";

type FieldOption = string | { label: string; value: string };

type ClientCustomField = {
  id: string;
  name: string;
  type: string;
  // Option rows with stable ids (QL-123); condition values store the id.
  options?: CustomFieldOption[] | null;
  isActive: boolean;
};

type ConditionalVisibilityFieldsProps = {
  enabled: boolean;
  onEnabledChange: (enabled: boolean) => void;
  sourceFieldId: string;
  onSourceFieldIdChange: (fieldId: string) => void;
  operator: ConditionalOperator;
  onOperatorChange: (operator: ConditionalOperator) => void;
  value: string;
  onValueChange: (value: string) => void;
  availableFields: FlowField[];
  clientCustomFields?: ClientCustomField[];
  disabled?: boolean;
  flowLanguage: string;
  label?: string;
  description?: string;
  pageType?: PageType; // Page type to filter goal-scoped fields
  /**
   * QPBI-321 — portfolio-scoped sources are only offerable where a current
   * portfolio exists at evaluation time. The page dialog passes false: a
   * page's own visibility never uses portfolio sources.
   */
  allowPortfolioSources?: boolean;
  /**
   * QL-120 — the flow's portfolio collection. The goal-type source is only
   * offered (and its picker only works) when the flow has one and it defines
   * at least one goal type.
   */
  portfolioCollectionId?: string | null;
  /**
   * QL-13 — the flow being edited, so quiz-result sources can be offered. Left
   * out means no quiz sources rather than an error: a picker that cannot know
   * its flow simply offers one fewer kind of rule.
   */
  flowId?: string | null;
  /**
   * Whether a source is one the configured component itself produces (own
   * flow field / produced sentinels / own quiz / any product-assessment
   * source) — removed from the picker, since a self-referential rule
   * deadlocks or trap-doors the component. A stored rule that already uses
   * one renders an inline warning instead of silently blanking. A predicate
   * rather than a set: the quiz and product-assessment cases are prefix
   * rules, decided by the shared `isSelfReferentialConditionSource`.
   */
  isSourceExcluded?: (sourceId: string) => boolean;
};

export function ConditionalVisibilityFields({
  enabled,
  onEnabledChange,
  sourceFieldId,
  onSourceFieldIdChange,
  operator: storedOperator,
  onOperatorChange,
  value,
  onValueChange,
  availableFields,
  clientCustomFields = [],
  disabled = false,
  flowLanguage,
  label,
  description,
  pageType = "global",
  allowPortfolioSources = true,
  portfolioCollectionId = null,
  isSourceExcluded,
  flowId = null,
}: ConditionalVisibilityFieldsProps) {
  const { tenantDefaultLanguage } = useUserLanguage();
  const t = pickT(T, flowLanguage);
  const countryOptions = useCountryOptions();
  const isExcluded = (sourceId: string) => isSourceExcluded?.(sourceId) ?? false;
  // QL-161 — a source is offered only where its scope is evaluable on this
  // page (`global < goal < portfolio`), decided by the shared registry +
  // predicate so the picker and the server guard cannot drift apart.
  const fieldScopeById = new Map(
    availableFields.map((f) => [f.id, (f.scope || "global") as ConditionScope]),
  );
  const canOffer = (sourceId: string) =>
    !isExcluded(sourceId) &&
    isConditionAllowedInScope(
      resolveConditionSourceScope(sourceId, (fieldId) => fieldScopeById.get(fieldId)),
      pageType,
    );
  // QL-120 — goal type additionally needs the flow to have a collection that
  // defines types (otherwise every goal is "No type" and any rule would be
  // trivially true). Kept offerable while loading so an existing rule's
  // source does not blink out of the select.
  const { goalTypes: collectionGoalTypes, isLoading: goalTypesLoading } = useGoalTypes(
    portfolioCollectionId ?? null,
  );
  // QL-13 — the flow's own `quiz` components. A quiz is authored inside one, so
  // the flow decides what a rule here can name. Global scope, offerable on
  // every page type.
  const { quizzes: activeQuizzes, isUnknown: quizzesUnknown } = useFlowQuizzes(flowId);
  // QL-361 — the collection's active product types: one status source per
  // configured mode, or one Simple (Yes / No) source for a type with neither
  // (QL-541). Global scope, like the quizzes.
  const { configuredAssessmentTypes, isUnknown: assessmentTypesUnknown } =
    useFlowConfiguredAssessmentTypes(flowId);
  const canOfferGoalType =
    canOffer(GOAL_TYPE_SOURCE) &&
    !!portfolioCollectionId &&
    (goalTypesLoading || collectionGoalTypes.length > 0);
  const isGoalTypeSource = sourceFieldId === GOAL_TYPE_SOURCE;
  // Fallback labels for when label/description props are not provided
  const labelText = label ?? t.conditionalVisibility;
  const descriptionText = description ?? t.conditionalVisibilityDescription;

  // Flow fields obey the same scope rule as sentinels (via their `scope` column)
  const filteredFlowFields = availableFields.filter((field) => canOffer(field.id));
  // Clear value when source field changes (different fields have different valid values)
  // and keep the operator valid for the new source.
  const handleSourceFieldChange = (newFieldId: string) => {
    onSourceFieldIdChange(newFieldId);
    onValueChange("");
    const newCustomField = newFieldId.startsWith("__custom_field_")
      ? clientCustomFields.find((f) => f.id === newFieldId.replace("__custom_field_", ""))
      : undefined;
    const fieldType =
      newCustomField?.type ?? availableFields.find((f) => f.id === newFieldId)?.type ?? "";
    const next = operatorForNewSource(operator, { sourceId: newFieldId, fieldType });
    if (next !== operator) onOperatorChange(next);
  };

  // Get options for special fields and dropdown fields
  const getFieldOptions = (fieldId: string): FieldOption[] => {
    // Special field: Classification Type
    if (fieldId === "__classification_type__") {
      return ["Retail", "Professional", "Eligible Counterparty"];
    }

    // Special field: Client Country
    if (fieldId === "__client_country__") {
      // QL-323 — values are the ISO codes the client record holds (a name
      // could never equal `client.country`); labels localized and sorted with
      // the Nordic markets first, the same list the client form uses.
      return [...countryOptions.priority, ...countryOptions.rest].map((o) => ({
        value: o.code,
        label: o.label,
      }));
    }

    // Special field: Client Type
    if (fieldId === "__client_type__") {
      return [
        { label: t.person, value: "Person" },
        { label: t.company, value: "Company" },
        { label: t.group, value: "Group" },
      ];
    }

    // Portfolio-scoped field: Portfolio Type (QPBI-321)
    if (fieldId === PORTFOLIO_TYPE_SOURCE) {
      return [
        { label: t.modelPortfolio, value: "model" },
        { label: t.customPortfolio, value: "custom" },
        { label: t.dynamicPortfolio, value: "dynamic" },
      ];
    }

    // Portfolio-scoped field: Schedule Type (QPBI-321)
    if (fieldId === PORTFOLIO_SCHEDULE_SOURCE) {
      return [
        { label: t.oneTime, value: "one_time" },
        { label: t.recurring, value: "recurring" },
      ];
    }

    // QL-774 — session-level: a follow-up or new advice
    if (fieldId === FOLLOWUP_MODE_SOURCE) {
      return [
        { label: t.yes, value: "true" },
        { label: t.no, value: "false" },
      ];
    }

    // Special field: Has Risk Assessment
    if (fieldId === "__goal_has_risk__") {
      return [
        { label: t.yes, value: "true" },
        { label: t.no, value: "false" },
      ];
    }

    // Goal-scoped field: Risk Score Capped (QL-71) — four server-derived states
    if (fieldId === GOAL_RISK_SCORE_CAPPED_SOURCE) {
      return [
        { label: t.yes, value: "yes" },
        { label: t.no, value: "no" },
        { label: t.noRiskAssessment, value: "no_risk_assessment" },
        { label: t.capNotAssessed, value: "no_client_field_data" },
      ];
    }

    // Special field: Trapets PEP Status
    if (fieldId === "__trapets_pep__") {
      return [
        { label: t.unchecked, value: "unchecked" },
        { label: t.yes, value: "yes" },
        { label: t.no, value: "no" },
      ];
    }

    // Special field: Trapets Sanctioned Status
    if (fieldId === "__trapets_sanctioned__") {
      return [
        { label: t.unchecked, value: "unchecked" },
        { label: t.yes, value: "yes" },
        { label: t.no, value: "no" },
      ];
    }

    // Special field: Sumsub Verified Status
    if (fieldId === "__sumsub_verified__") {
      return [
        { label: t.unchecked, value: "unchecked" },
        { label: t.yes, value: "yes" },
        { label: t.no, value: "no" },
      ];
    }

    // QL-68 — Sumsub document country: the same localised, Nordic-first list
    // as Client Country, but the VALUE is the alpha-3 code Sumsub reports so
    // the rule compares raw against the snapshot.
    if (fieldId === "__sumsub_document_country__") {
      return [...countryOptions.priority, ...countryOptions.rest].map((o) => ({
        value: o.alpha3,
        label: o.label,
      }));
    }

    // QL-13 — the three quiz statuses. Rules compare the raw status string;
    // the label is only what the designer reads.
    if (isQuizResultSource(fieldId)) {
      return quizResultValueOptions(flowLanguage);
    }

    // QL-361 — Passed / Not passed / Not answered, or Incomplete for Combined.
    if (isProductAssessmentSource(fieldId)) {
      return assessmentSourceValueOptions(fieldId, flowLanguage);
    }

    // Boolean fields (flow or client custom) offer Yes/No values
    const booleanOptions: FieldOption[] = [
      { label: t.yes, value: "true" },
      { label: t.no, value: "false" },
    ];

    // Client custom fields (option-based: dropdown/radio/multiselect, or boolean)
    if (fieldId.startsWith("__custom_field_")) {
      const customFieldId = fieldId.replace("__custom_field_", "");
      const customField = clientCustomFields.find((f) => f.id === customFieldId);
      if (customField?.type === "boolean") {
        return booleanOptions;
      }
      if (customField && isOptionFieldType(customField.type) && customField.options) {
        // Rule values store the option id (QL-123); show the localized label.
        // Archived options stay listed only while the rule still uses them.
        return customField.options
          .filter((o) => !o.isArchived || o.id === value)
          .map((o) => ({
            value: o.id,
            label: getLocalizedValue(o.labelI18n, flowLanguage, tenantDefaultLanguage) || o.id,
          }));
      }
      return [];
    }

    // Regular flow field - option-based (dropdown/radio/multiselect) or boolean
    const field = availableFields.find((f) => f.id === fieldId);
    if (field?.type === "boolean") {
      return booleanOptions;
    }
    if (!field || !isOptionFieldType(field.type)) {
      return [];
    }

    // Rule values store the option id (QL-201); show the label. Archived
    // options stay listed only while the rule still uses them.
    return (field.options ?? [])
      .filter((o) => !o.isArchived || o.id === value)
      .map((o) => ({ value: o.id, label: o.label }));
  };

  // Helper to get option value (handles both string and object options)
  const getOptionValue = (option: FieldOption): string =>
    typeof option === "string" ? option : option.value;

  // Helper to get option label (handles both string and object options)
  const getOptionLabel = (option: FieldOption): string =>
    typeof option === "string" ? option : option.label;

  // Resolve the source field's type (client custom field or flow field)
  const sourceCustomField = sourceFieldId.startsWith("__custom_field_")
    ? clientCustomFields.find((f) => f.id === sourceFieldId.replace("__custom_field_", ""))
    : undefined;
  const sourceField = availableFields.find((f) => f.id === sourceFieldId);
  const sourceFieldType = sourceCustomField?.type ?? sourceField?.type ?? "";

  // Check if this is a client custom option field (dropdown/radio/multiselect)
  const isCustomOptionField = !!sourceCustomField && isOptionFieldType(sourceCustomField.type);

  // Multiselect answers are sets — membership operators instead of equality
  const isMultiselectField = sourceFieldType === "multiselect";

  // Boolean answers are Yes/No — equality only, values from a fixed list
  const isBooleanField = sourceFieldType === "boolean";

  // QL-361 — score sources compare a number: Equals / Greater than / Less than
  // only, no "is empty" pair ("has the client answered" is a status question).
  const isScoreSource = isScoreConditionSource(sourceFieldId);

  // Fields that should only show equals/not_equals (no numeric comparisons)
  const isEnumField =
    sourceFieldId === "__classification_type__" ||
    sourceFieldId === "__client_country__" ||
    sourceFieldId === "__client_type__" ||
    sourceFieldId === FOLLOWUP_MODE_SOURCE ||
    sourceFieldId === PORTFOLIO_TYPE_SOURCE ||
    sourceFieldId === PORTFOLIO_SCHEDULE_SOURCE ||
    sourceFieldId === "__goal_has_risk__" ||
    sourceFieldId === GOAL_RISK_SCORE_CAPPED_SOURCE ||
    sourceFieldId === "__trapets_pep__" ||
    sourceFieldId === "__trapets_sanctioned__" ||
    sourceFieldId === "__sumsub_verified__" ||
    sourceFieldId === "__sumsub_document_country__" ||
    isQuizResultSource(sourceFieldId) ||
    isProductAssessmentSource(sourceFieldId) ||
    isCustomOptionField ||
    isBooleanField;
  // A score source gets its own menu even when an enum rule above covers it.
  const scalarOperators = scalarConditionOperators(sourceFieldId, isEnumField, sourceFieldType);
  // QL-237 / QL-254 — a stored rule whose operator the menu no longer lists
  // shows the one a switch to its source would pick, and hands it to the
  // parent so that is what gets saved.
  const operator =
    isGoalTypeSource || isMultiselectField
      ? storedOperator
      : menuConditionOperator(storedOperator, scalarOperators, {
          sourceId: sourceFieldId,
          fieldType: sourceFieldType,
        });
  useEffect(() => {
    if (operator !== storedOperator) onOperatorChange(operator);
  }, [operator, storedOperator, onOperatorChange]);

  // Determine if we should show the value input
  const showValueInput = isGoalTypeSource
    ? operator === "in" || operator === "not_in"
    : isMultiselectField
      ? operator === "contains" || operator === "not_contains"
      : scalarOperators.includes(operator) && isValueRequiredOperator(operator);

  // Check if the value can be picked from a fixed option list
  const isDropdownField =
    isOptionFieldType(sourceField?.type ?? "") || sourceField?.type === "boolean" || isEnumField;
  const dropdownOptions = sourceFieldId ? getFieldOptions(sourceFieldId) : [];

  // QL-323 — the source picker is searchable: a flat option list with the same
  // entries, in the same order, the grouped Select used to render.
  const sourceOptions: SearchableSelectOption[] = [];
  const offer = (value: string, label: string, group?: string) =>
    sourceOptions.push({ value, label, group });
  // Special fields — `canOffer` = not produced by this component AND allowed in
  // this page's scope (all of these are global today; the check keeps a future
  // non-global sentinel from leaking, same as the goal/portfolio sources below).
  if (canOffer(FOLLOWUP_MODE_SOURCE)) offer(FOLLOWUP_MODE_SOURCE, t.followupMode);
  if (canOffer("__client_country__")) offer("__client_country__", t.clientCountry);
  if (canOffer("__client_type__")) offer("__client_type__", t.clientType);
  if (canOffer("__classification_type__")) offer("__classification_type__", t.classificationType);
  if (canOffer("__trapets_pep__")) offer("__trapets_pep__", t.trapetsPep);
  if (canOffer("__trapets_sanctioned__")) offer("__trapets_sanctioned__", t.trapetsSanctioned);
  if (canOffer("__sumsub_verified__")) offer("__sumsub_verified__", t.sumsubVerified);
  if (canOffer("__sumsub_document_country__"))
    offer("__sumsub_document_country__", t.sumsubDocumentCountry);
  // Goal-scoped sources — only where a current goal exists at evaluation time
  // (goal/portfolio pages, QL-161)
  if (canOffer(GOAL_RISK_SCORE_SOURCE)) offer(GOAL_RISK_SCORE_SOURCE, t.goalRiskScore);
  if (canOffer(GOAL_HAS_RISK_SOURCE)) offer(GOAL_HAS_RISK_SOURCE, t.goalHasRisk);
  if (canOffer(GOAL_RISK_SCORE_CAPPED_SOURCE))
    offer(GOAL_RISK_SCORE_CAPPED_SOURCE, t.goalRiskScoreCapped);
  if (canOfferGoalType) offer(GOAL_TYPE_SOURCE, t.goalType);
  // Portfolio-scoped sources — only where a current portfolio exists at
  // evaluation time (QPBI-321 / QL-161)
  // Each source passes its own offer check, so excluding one (a component
  // that produces it) never drags the other out of, or into, the list.
  if (allowPortfolioSources) {
    if (canOffer(PORTFOLIO_TYPE_SOURCE)) offer(PORTFOLIO_TYPE_SOURCE, t.portfolioType);
    if (canOffer(PORTFOLIO_SCHEDULE_SOURCE))
      offer(PORTFOLIO_SCHEDULE_SOURCE, t.portfolioScheduleType);
  }
  // QL-13 — one entry per quiz on this flow; global scope, so only the
  // self-reference exclusion can drop one (QL-361: a quiz component never
  // sees its own quiz, every other quiz stays available)
  for (const quiz of activeQuizzes) {
    const sourceId = `${QUIZ_RESULT_SOURCE_PREFIX}${quiz.id}`;
    if (!canOffer(sourceId)) continue;
    offer(sourceId, `${t.quizField} ${quiz.name}`);
  }
  // QL-361 — `Quiz: <name> · Score`, one per quiz; a quiz component never sees
  // its own score either
  for (const entry of buildQuizScorePickerEntries(activeQuizzes, flowLanguage)) {
    if (canOffer(entry.id)) offer(entry.id, entry.label);
  }
  // Client custom fields — every type but "user" (QL-254)
  for (const field of clientCustomFields) {
    if (field.isActive && isConditionSourceCustomFieldType(field.type)) {
      offer(`__custom_field_${field.id}`, `${t.clientField} ${field.name} (${field.type})`);
    }
  }
  // Regular flow fields (filtered by scope and page type)
  for (const field of filteredFlowFields) {
    const goalTag = field.scope === "goal" ? ` ${t.goal}` : "";
    offer(field.id, `${t.flowField} ${field.name} (${field.type})${goalTag}`);
  }
  // QL-361 — `Bonds · Quiz · Status` / `· Score`, only for the modes each type has configured.
  // Offered last: it is the only grouped run, and SearchableSelect never closes a heading, so
  // any ungrouped entry after it would render under "Product assessment".
  for (const entry of buildAssessmentPickerEntries(configuredAssessmentTypes, {
    uiLanguage: flowLanguage,
    nameLanguage: flowLanguage,
    fallbackLanguage: tenantDefaultLanguage,
    valueLanguage: flowLanguage,
  })) {
    if (canOffer(entry.id)) offer(entry.id, entry.label, entry.group);
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <Label htmlFor="conditional-enabled" className="text-base">
            {labelText}
          </Label>
          <p className="text-sm text-muted-foreground">{descriptionText}</p>
        </div>
        <Switch
          id="conditional-enabled"
          checked={enabled}
          onCheckedChange={onEnabledChange}
          disabled={disabled}
        />
      </div>

      {enabled && (
        <div className="pl-4 space-y-4 border-l-2">
          {/* Source Field */}
          <div className="space-y-2">
            <Label htmlFor="source-field">
              {t.whenField} <span className="text-destructive">*</span>
            </Label>
            <SearchableSelect
              id="source-field"
              value={sourceFieldId}
              onValueChange={handleSourceFieldChange}
              options={sourceOptions}
              placeholder={t.selectField}
              searchPlaceholder={t.searchFields}
              emptyMessage={t.noFieldsAvailable}
              noResultsMessage={t.noFieldsMatch}
              disabled={disabled}
            />
            {/* A stored rule may predate the self-reference guard — explain
                instead of silently blanking the (now unofferable) selection. */}
            {sourceFieldId && isExcluded(sourceFieldId) && (
              <p className="text-sm text-destructive">{t.selfSourceWarning}</p>
            )}
            {/* QL-13 — deleting a quiz component deletes the quiz, and nothing
                rewrites the rules naming it. The select renders blank for one
                of those and the rule can never be true again, so name the
                problem here instead of letting the content quietly vanish. */}
            {!quizzesUnknown && isDanglingQuizSource(sourceFieldId, activeQuizzes) && (
              <p className="text-xs text-destructive">
                {conditionRuleProblemMessage("missing_quiz", flowLanguage)}
              </p>
            )}
            {/* QL-361 — likewise for a product type or mode that is no longer
                configured on the flow's collection (deleted, deactivated, mode
                switched off, flow repointed). */}
            {!assessmentTypesUnknown &&
              isDanglingProductAssessmentSource(sourceFieldId, configuredAssessmentTypes) && (
                <p className="text-xs text-destructive">
                  {conditionRuleProblemMessage("missing_assessment", flowLanguage)}
                </p>
              )}
          </div>

          {/* Operator */}
          <div className="space-y-2">
            <Label htmlFor="operator">{t.condition}</Label>
            <Select
              value={operator}
              onValueChange={(v) => onOperatorChange(v as ConditionalOperator)}
              disabled={disabled}
            >
              <SelectTrigger id="operator">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {isGoalTypeSource ? (
                  // QL-120 — set operators only; a single tick is the equality case.
                  <>
                    <SelectItem value="in">{t.isOneOf}</SelectItem>
                    <SelectItem value="not_in">{t.isNotOneOf}</SelectItem>
                  </>
                ) : isMultiselectField ? (
                  <>
                    <SelectItem value="contains">{t.contains}</SelectItem>
                    <SelectItem value="not_contains">{t.notContains}</SelectItem>
                    <SelectItem value="is_empty">{t.isEmpty}</SelectItem>
                    <SelectItem value="is_not_empty">{t.isNotEmpty}</SelectItem>
                  </>
                ) : (
                  <ScalarOperatorItems operators={scalarOperators} />
                )}
              </SelectContent>
            </Select>
          </div>

          {/* Value */}
          {showValueInput && (
            <div className="space-y-2">
              <Label htmlFor="condition-value">
                {t.value} <span className="text-destructive">*</span>
              </Label>
              {isGoalTypeSource && portfolioCollectionId ? (
                <GoalTypeConditionPicker
                  portfolioCollectionId={portfolioCollectionId}
                  value={value}
                  onChange={onValueChange}
                  language={flowLanguage}
                  disabled={disabled}
                />
              ) : isScoreSource ? (
                <ScoreConditionValueInput
                  id="condition-value"
                  value={value}
                  onChange={onValueChange}
                  passScore={scoreSourcePassScore(
                    sourceFieldId,
                    configuredAssessmentTypes,
                    activeQuizzes,
                  )}
                  language={flowLanguage}
                  disabled={disabled}
                />
              ) : sourceFieldId === "__client_country__" ||
                sourceFieldId === "__sumsub_document_country__" ? (
                <SearchableSelect
                  id="condition-value"
                  value={value}
                  onValueChange={onValueChange}
                  options={dropdownOptions.map((option) => ({
                    value: getOptionValue(option),
                    label: getOptionLabel(option),
                  }))}
                  placeholder={t.selectValue}
                  searchPlaceholder={t.searchCountries}
                  noResultsMessage={t.noCountriesMatch}
                  disabled={disabled}
                />
              ) : isDropdownField && dropdownOptions.length > 0 ? (
                <Select value={value} onValueChange={onValueChange} disabled={disabled}>
                  <SelectTrigger id="condition-value">
                    <SelectValue placeholder={t.selectValue} />
                  </SelectTrigger>
                  <SelectContent>
                    {dropdownOptions.map((option) => (
                      <SelectItem key={getOptionValue(option)} value={getOptionValue(option)}>
                        {getOptionLabel(option)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : sourceFieldType === "date" ? (
                // QL-209 — the same picker the date answer uses, so the rule
                // value is stored as yyyy-MM-dd like the answer it compares to.
                <DatePicker
                  id="condition-value"
                  value={value}
                  onChange={onValueChange}
                  disabled={disabled}
                />
              ) : sourceFieldType === "datetime" ? (
                // QL-211 — the same picker the datetime answer uses, so the
                // rule value is stored as UTC YYYY-MM-DDTHH:mm:ssZ.
                <DateTimePicker
                  id="condition-value"
                  value={value}
                  onChange={onValueChange}
                  disabled={disabled}
                />
              ) : (
                <Input
                  id="condition-value"
                  value={value}
                  onChange={(e) => onValueChange(e.target.value)}
                  placeholder={t.enterValue}
                  disabled={disabled}
                />
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
